/**
 * @file The Detail cell of one fetch part: why it failed, which product to use, or when it landed
 * in the table. Consumer: `SignifyFetchJobPartTable`. It subscribes to its own part alone.
 * This row's Retry sends its own `docId` as the request's only `partId`, so it resets this part and
 * nothing else; the footer button is what resets every failed part. It carries none of the unsent
 * candidate choices either — those travel with the footer button alone.
 * The error text is the only cell allowed to wrap onto a second line; `rc-virtual-list` measures
 * rows, so the taller failed rows cost nothing.
 */

import { FC } from 'react';
import { Button, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import FlexCol from '@/components/atoms/FlexCol';
import type { SignifyFetchPart } from '#types';
import SignifyFetchJobCandidatePicker from './SignifyFetchJobCandidatePicker';
import type { SignifyFetchJobPicks } from '../index.types';

const { Text } = Typography;

type SignifyFetchJobPartDetailCellProps = {
  part: SignifyFetchPart;
  picks: SignifyFetchJobPicks;
  retryDisabledReason: string | null;
  onRetryPart: (partId: string) => void;
};

const SignifyFetchJobPartDetailCell: FC<SignifyFetchJobPartDetailCellProps> = ({
  part,
  picks,
  retryDisabledReason,
  onRetryPart,
}) => {
  const snap = useSnapshot(part);

  if (snap.state === 'failed') {
    return (
      <FlexCol gap={5}>
        <Text type="danger">{snap.error}</Text>
        <Button
          size="small"
          disabled={!!retryDisabledReason}
          aria-label={
            retryDisabledReason
              ? `signify-fetch-job-retry-${snap.docId} (${retryDisabledReason})`
              : `signify-fetch-job-retry-${snap.docId}`
          }
          onClick={() => onRetryPart(snap.docId)}
        >
          Retry this part
        </Button>
      </FlexCol>
    );
  }

  if (snap.state === 'ambiguous') {
    return (
      <SignifyFetchJobCandidatePicker
        partId={snap.docId}
        candidates={snap.candidates}
        picks={picks}
      />
    );
  }

  if (snap.state === 'done' && snap.insertedAt !== null) {
    return (
      <Text type="secondary">
        Inserted {new Date(snap.insertedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Text>
    );
  }

  return null;
};

export default SignifyFetchJobPartDetailCell;
